import React, {
  memo,
  useMemo,
} from 'react';

import { Typography } from 'antd';
import moment from 'moment';
import { IoChevronForward } from 'react-icons/io5';
import { PiReceiptFill } from 'react-icons/pi';

import API from '../../../../api';
import { useTheme } from '../../../../context/ThemeProvider';
import showAppError from '../../../../shared/error';
import { useAppSelector } from '../../../../shared/hooks';
import { newNumberFormat } from '../../../../shared/utils';
import { IFlexiPayInstallment } from '../../../../types/userTypes';
import { IRenewFlexiMango } from './types';

interface Props {
  activeFlexiMangoes: IRenewFlexiMango[];
}

type InstallmentDetail =
  | IFlexiPayInstallment
  | {
      installmentNumber: number;
      amount?: Record<string, number>;
    }
  | null;

const getCurrencyCodeFromMango = (mango: IRenewFlexiMango['mango']) => {
  const c = mango?.currency;
  if (c === 'INR') return 'inr';
  if (c === 'USD') return 'usd';
  if (c === 'EUR') return 'eur';
  return 'inr';
};

const numberFormatForMangoCurrency = (
  currency?: string,
): 'NUM_INR' | 'NUM_US' | 'NUM_EUR' => {
  if (currency === 'USD') return 'NUM_US';
  if (currency === 'EUR') return 'NUM_EUR';
  return 'NUM_INR';
};

const getSymbolForMangoCurrency = (mango: IRenewFlexiMango['mango']) => {
  const c = mango?.currency;
  if (c === 'EUR') return '\u20AC';
  if (c === 'USD') return '$';
  return '\u20B9';
};

const getInstallmentKey = (d: IFlexiPayInstallment) =>
  d.installmentNumber ?? d.installmentIndex ?? null;

const getNextInstallmentDetail = (item: IRenewFlexiMango) => {
  const nextNum = (item?.flexiPayCurrentInstallmentNumber ?? 0) + 1;
  const fromDetails = item?.flexiPayInstallMentDetails?.find(
    (d) => getInstallmentKey(d) === nextNum,
  );
  if (fromDetails) return { nextNum, detail: fromDetails as InstallmentDetail };
  const fromConfig = item?.mango?.flexiPayConfig?.installments?.find(
    (d) => d.installmentNumber === nextNum,
  );
  return { nextNum, detail: (fromConfig || null) as InstallmentDetail };
};

const getAmountFromDetail = (
  mango: IRenewFlexiMango['mango'],
  detail: NonNullable<InstallmentDetail>,
) => {
  if (!detail?.amount || typeof detail.amount !== 'object') return null;
  const key = getCurrencyCodeFromMango(mango);
  const amt = detail.amount;
  if (amt[key as keyof typeof amt] != null) {
    return amt[key as keyof typeof amt] as number;
  }
  const upper = key.toUpperCase() as keyof typeof amt;
  if (amt[upper] != null) return amt[upper] as number;
  return amt.inr ?? amt.usd ?? amt.eur ?? amt.INR ?? amt.USD ?? amt.EUR ?? null;
};

const formatInstallmentAmount = (
  mango: IRenewFlexiMango['mango'],
  detail: InstallmentDetail,
) => {
  if (!detail) return null;
  const raw = getAmountFromDetail(mango, detail);
  if (raw == null) return null;
  return newNumberFormat(raw, numberFormatForMangoCurrency(mango?.currency));
};

interface CardProps {
  item: IRenewFlexiMango;
}

const FlexiRenewMangoCard: React.FC<CardProps> = ({ item }) => {
  const { colors: COLORS } = useTheme();
  const { deeplinkUrl } = useAppSelector((state) => state.app);

  const { nextNum, detail } = useMemo(
    () => getNextInstallmentDetail(item),
    [item],
  );

  const mango = item?.mango;
  const title = mango?.title ?? '';
  const total = item?.flexiPayTotalInstallments;
  const dueMoment = moment(item?.flexiPayNextDueDate);
  const dueValid = dueMoment.isValid();
  const isDueToday = dueValid && dueMoment.isSame(moment(), 'day');
  const isOverdue = dueValid && dueMoment.isBefore(moment(), 'day');
  const dueDateFormatted = dueValid ? dueMoment.format('MMMM D, YYYY') : '';

  const amountStr = formatInstallmentAmount(mango, detail);
  const symbol = getSymbolForMangoCurrency(mango);

  const priceDisplay =
    amountStr != null ? (
      <strong className="inr">
        {symbol}
        {amountStr}
      </strong>
    ) : (
      <strong className="inr">
        {symbol}
        {newNumberFormat(
          mango?.price ?? 0,
          numberFormatForMangoCurrency(mango?.currency),
        )}
      </strong>
    );

  const pay = async () => {
    let url = `${deeplinkUrl}web/checkout/${mango?._id}`;
    url += '?purchaseNow=true';
    try {
      const resp = await API.fetchNewToken(undefined, 1800);
      if (resp?.status === 200) {
        const sessionToken = resp.data.result.accessToken;
        const params = new URLSearchParams();
        if (sessionToken) {
          params.set('sessionToken', encodeURIComponent(sessionToken));
        }
        url += params?.toString() ? `?${params.toString()}` : '';
        window.open(url);
      } else {
        showAppError(resp?.data);
      }
    } catch (err) {
      showAppError(err);
    }
  };

  const onCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void pay();
    }
  };

  return (
    <div
      className="feed-card primary feed-flexi-active-mango"
      role="button"
      tabIndex={0}
      onClick={() => {
        void pay();
      }}
      onKeyDown={onCardKeyDown}>
      <div className="feed-card__icon-bar">
        {isDueToday ? (
          <div className="feed-flexi-active-mango__alert-icon" aria-hidden>
            !
          </div>
        ) : (
          <PiReceiptFill size={22} color={COLORS.BACKGROUND} aria-hidden />
        )}
      </div>
      <div className="feed-card__details-wrapper feed-flexi-active-mango__row">
        <div className="feed-card-details">
          {isDueToday ? (
            <Typography.Text className="feed-flexi-active-mango__text">
              Your installment payment of {priceDisplay} for{' '}
              <strong>{title}</strong> is <strong>due today</strong>. Complete
              the payment to continue your access.
            </Typography.Text>
          ) : (
            <Typography.Text className="feed-flexi-active-mango__text">
              {isOverdue ? (
                <>
                  Your installment for <strong>{title}</strong> Installment{' '}
                  <strong>
                    {nextNum} of {total}
                  </strong>{' '}
                  worth {priceDisplay} was due on{' '}
                  <strong>{dueDateFormatted}</strong>. Pay now to keep your
                  access.
                </>
              ) : (
                <>
                  Your next installment for <strong>{title}</strong> Installment{' '}
                  <strong>
                    {nextNum} of {total}
                  </strong>{' '}
                  worth {priceDisplay} is due on{' '}
                  <strong>{dueDateFormatted}</strong>.
                </>
              )}
            </Typography.Text>
          )}
        </div>
        <div
          className="feed-card__arrow feed-flexi-active-mango__chevron"
          aria-hidden>
          <IoChevronForward size={24} color={COLORS.ICON} />
        </div>
      </div>
    </div>
  );
};

const RenewFlexiActiveMangoes: React.FC<Props> = ({ activeFlexiMangoes }) => {
  if (!activeFlexiMangoes?.length) {
    return null;
  }

  return (
    <>
      {activeFlexiMangoes.map((item) => (
        <FlexiRenewMangoCard key={item._id} item={item} />
      ))}
    </>
  );
};

export default memo(RenewFlexiActiveMangoes);
